import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { apiFetch, isApiError } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/keys';
import { useAuth } from '@/lib/auth';
import { useGuestWishlist } from './guest-store';

const ANONYMOUS = 'anonymous';
const IDS_STALE_MS = 5 * 60_000;

/**
 * Is this product on the visitor's wishlist? Signed in → the account's list (`GET /me/wishlist/ids`,
 * one request shared by every heart on the page); signed out → the list kept on this device.
 * `select` makes each heart re-render only when *its own* answer changes.
 */
export function useIsWishlisted(productId: string): boolean {
  const { user, isAuthenticated } = useAuth();
  const guestSaved = useGuestWishlist((state) => state.ids.includes(productId));
  const { data: accountSaved } = useQuery({
    queryKey: queryKeys.me.wishlistIds(user?.id ?? ANONYMOUS),
    queryFn: ({ signal }) => apiFetch<string[]>('/me/wishlist/ids', { signal }),
    enabled: isAuthenticated,
    staleTime: IDS_STALE_MS,
    select: (ids) => ids.includes(productId),
  });
  return isAuthenticated ? (accountSaved ?? false) : guestSaved;
}

interface ToggleVariables {
  productId: string;
  save: boolean;
}

interface ToggleContext {
  key: readonly unknown[];
  previous: string[] | undefined;
}

/**
 * `toggle(productId, save)` — save or remove one product. Signed in it is optimistic (the heart fills
 * at once, rolls back if the server refuses); signed out it only touches the local list.
 */
export function useToggleWishlist(): (productId: string, save: boolean) => void {
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const setGuestSaved = useGuestWishlist((state) => state.setSaved);
  const userId = user?.id ?? ANONYMOUS;

  const mutation = useMutation<void, Error, ToggleVariables, ToggleContext>({
    mutationFn: ({ productId, save }) =>
      apiFetch<void>(`/me/wishlist/${productId}`, { method: save ? 'PUT' : 'DELETE' }),
    onMutate: async ({ productId, save }) => {
      const key = queryKeys.me.wishlistIds(userId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<string[]>(key);
      queryClient.setQueryData<string[]>(key, (ids = []) => {
        const rest = ids.filter((id) => id !== productId);
        return save ? [productId, ...rest] : rest;
      });
      return { key, previous };
    },
    onError: (_error, _variables, context) => {
      if (context) queryClient.setQueryData(context.key, context.previous);
    },
    onSettled: () => {
      // the ids and the saved-products page are one family (`['me','wishlist', …]`)
      void queryClient.invalidateQueries({ queryKey: queryKeys.me.wishlist() });
    },
  });

  return (productId, save) => {
    if (isAuthenticated) mutation.mutate({ productId, save });
    else setGuestSaved(productId, save);
  };
}

/**
 * Mount once (app providers):
 *   1. reads the on-device list after the first client render;
 *   2. when the visitor signs in, merges that list into their account and clears it — so hearts
 *      pressed before signing in are not lost. The server side is idempotent (`PUT` = upsert), so a
 *      repeated or interrupted merge is harmless.
 */
export function useWishlistSync(): void {
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const hydrated = useGuestWishlist((state) => state.hydrated);
  const guestIds = useGuestWishlist((state) => state.ids);
  const merging = useRef(false);

  useEffect(() => {
    useGuestWishlist.getState().hydrate();
  }, []);

  const userId = user?.id;
  useEffect(() => {
    if (!isAuthenticated || !userId || !hydrated || guestIds.length === 0 || merging.current) {
      return;
    }
    merging.current = true;
    const pending = [...guestIds];
    void (async () => {
      const merged: string[] = [];
      for (const productId of pending) {
        try {
          await apiFetch<void>(`/me/wishlist/${productId}`, { method: 'PUT' });
          merged.push(productId);
        } catch (error) {
          // A product that has been unpublished since (404/422) can never be saved: drop it. Anything
          // else — a dropped connection, a 5xx, an expired session — keeps it for the next attempt.
          if (isApiError(error) && (error.status === 404 || error.status === 422)) {
            merged.push(productId);
          }
        }
      }
      useGuestWishlist.getState().remove(merged);
      await queryClient.invalidateQueries({ queryKey: queryKeys.me.wishlist() });
      merging.current = false;
    })();
  }, [isAuthenticated, userId, hydrated, guestIds, queryClient]);
}
